import { Flex } from "antd";
import { FC, useCallback, useEffect, useState } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { getAreaInfo, postMarketChangeAddress } from "tm-shared/api";
import { Market, Option } from "tm-shared/types";
import { buildRoutePath } from "tm-shared/utils";

import BaseButton from "@/components/BaseButton";
import Header from "@/components/Header";
import { MainContainer } from "@/components/Layout/Styled";
import { AddAddressModal } from "@/components/Modals/AddAddressModal";
import { DuplicateByAddressModal } from "@/components/Modals/DuplicateByAddressModal";
import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";
import { ROUTES } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { setAddressVerifyModalSelectionPayload } from "@/store/slices/ui";
import { AddressVerifyFormValues } from "@/types/form";
import {
    AddAddressModalPayload,
    DuplicateByAddressModalPayload,
} from "@/types/modals";

import { AddressSection } from "./AddressSection";
import { StyledForm } from "./Styled";

type ModalState =
    | {
          type: "addAddress";
          payload: AddAddressModalPayload;
      }
    | {
          type: "duplicateByAddress";
          payload: DuplicateByAddressModalPayload;
      }
    | null;

interface MarketVerifyAddressPageProps {
    market: Market;
}

export const MarketVerifyAddressPage: FC<MarketVerifyAddressPageProps> = ({
    market,
}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { addressVerifyModalSelectionPayload } = useAppSelector((s) => s.ui);

    const [isChecking, setIsChecking] = useState(false);
    const [optionCache, setOptionCache] = useState<Option[]>([]);
    const [modal, setModal] = useState<ModalState>(null);

    const [regionLabel, setRegionLabel] = useState<string>("");

    const regionId = market.address.mainRegionId;
    const marketId = market.id;

    const addressVerifyForm = useForm<AddressVerifyFormValues>({
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: {
            address: "",
        },
    });

    const {
        control,
        setValue,
        handleSubmit,
        formState: { isSubmitting },
    } = addressVerifyForm;

    const address = useWatch<AddressVerifyFormValues>({
        name: "address",
        control,
    });

    const onSubmit = useCallback(
        async (data: AddressVerifyFormValues) => {
            const { address, parentId } = data;

            await postMarketChangeAddress({
                market: marketId,
                addressId: address,
                ...(parentId && { parentId }),
            }),
                navigate(buildRoutePath(ROUTES.MarketDetails, { marketId }), {
                    replace: true,
                });
        },
        [marketId, navigate],
    );

    const handleCloseModal = useCallback(() => {
        setModal(null);
    }, []);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                const { result } = await getAreaInfo({ area: regionId });
                if (!cancelled) setRegionLabel(result.name);
            } catch (e) {
                if (!cancelled) setRegionLabel("");
            }
        };

        void load();

        return () => {
            cancelled = true;
        };
    }, [regionId]);

    useEffect(() => {
        if (addressVerifyModalSelectionPayload) {
            const { fieldName, option, value } =
                addressVerifyModalSelectionPayload;

            setValue(fieldName, value);

            if (option) {
                setOptionCache([option]);
            }

            dispatch(setAddressVerifyModalSelectionPayload(null));
        }
    }, [dispatch, addressVerifyModalSelectionPayload, setValue]);

    const onOpenAddAddressModal = useCallback(() => {
        setModal({
            type: "addAddress",
            payload: {
                region: regionLabel,
                regionId,
                variant: "verifyAddress",
            },
        });
    }, [regionId, regionLabel]);

    const onOpenDuplicateModal = useCallback(
        ({
            markets,
            addressLabel,
        }: {
            markets: Market[];
            addressLabel: string;
        }) => {
            setModal({
                type: "duplicateByAddress",
                payload: {
                    markets: markets,
                    address: addressLabel,
                    variant: "verifyAddress",
                },
            });
        },
        [],
    );

    const currentAddressLabel =
        optionCache.find((o) => o.value === address)?.label ?? "";

    return (
        <>
            <Header back title="Верифікація адреси" />

            <MainContainer>
                <FormProvider {...addressVerifyForm}>
                    <StyledForm
                        layout="vertical"
                        onFinish={handleSubmit(onSubmit)}
                    >
                        <Flex vertical gap={4}>
                            <SecondaryText>
                                Не верифікована адреса:
                            </SecondaryText>
                            <PrimaryText>{market.address.name}</PrimaryText>
                        </Flex>

                        <AddressSection
                            onOpenAddAddressModal={onOpenAddAddressModal}
                            onOpenDuplicateModal={onOpenDuplicateModal}
                            address={address}
                            regionId={regionId}
                            options={optionCache}
                            setOptions={setOptionCache}
                            isChecking={isChecking}
                            setIsChecking={setIsChecking}
                        />

                        {currentAddressLabel && (
                            <Flex vertical gap={4}>
                                <PrimaryText>Повна адреса</PrimaryText>
                                <PrimaryText fontWeight="500">
                                    {currentAddressLabel}
                                </PrimaryText>
                            </Flex>
                        )}

                        <Controller
                            name={"parentId"}
                            control={control}
                            render={() => <></>}
                        />

                        <Flex style={{ marginTop: "auto" }}>
                            <BaseButton
                                size="large"
                                type="primary"
                                htmlType="submit"
                                loading={isSubmitting}
                                disabled={isChecking}
                            >
                                Зберегти
                            </BaseButton>
                        </Flex>
                    </StyledForm>
                </FormProvider>
            </MainContainer>

            {modal?.type === "addAddress" && (
                <AddAddressModal
                    onClose={handleCloseModal}
                    payload={modal.payload}
                />
            )}
            {modal?.type === "duplicateByAddress" && (
                <DuplicateByAddressModal
                    onClose={handleCloseModal}
                    payload={modal.payload}
                />
            )}
        </>
    );
};
